<template>
    <toolbar
        :id="toolbarId"
        side="right"
        :collapse-offset="toolbarCollapseOffset"
        @click.native.stop="enableEvents"
    >
        <div class="toolbar-editor">
            <section class="toolbar-editor__column toolbar-editor__column--secondary">
                <toolbar-vertical ref="toolbarVertical" :page-id="pageId" :screen="screen" />
            </section>

            <section class="toolbar-editor__column toolbar-editor__column--main">
                <div v-if="pageData" class="toolbar-editor__language">
                    <language-menu :screen="screen" @load-language="loadLanguage" />
                </div>

                <ul class="toolbar-editor__tabs">
                    <li class="toolbar-editor__tab" :class="{ [STATE_CLASSES.SELECTED]: visible.insert }">
                        <button class="toolbar-editor__tab-button" type="button" @click="show('insert')">
                            INSERT
                        </button>
                    </li>

                    <li class="toolbar-editor__tab" :class="{ [STATE_CLASSES.SELECTED]: visible.options }">
                        <button class="toolbar-editor__tab-button" type="button" @click="show('options')">
                            OPTIONS
                        </button>
                    </li>
                </ul>

                <ul class="toolbar-editor__panes">
                    <li class="toolbar-editor__pane" :class="{ [STATE_CLASSES.VISIBLE]: visible.insert }">
                        <toolbar-insert :screen="screen" :toolbar="toolbarId" />
                    </li>

                    <li class="toolbar-editor__pane" :class="{ [STATE_CLASSES.VISIBLE]: visible.options }">
                        <toolbar-options :screen="screen" @unlock="onUnlockComponent" />
                    </li>
                </ul>
            </section>
        </div>
    </toolbar>
</template>

<script>
import { mapState, mapMutations, mapActions } from 'vuex';

import Toolbar from '@/components/utilities/toolbar';
import ToolbarInsert from '@/components/shared/toolbars/toolbar-insert';
import ToolbarOptions from '@/components/shared/toolbars/toolbar-options';
import ToolbarVertical from '@/components/shared/toolbars/toolbar-vertical';
import LanguageMenu from '@/components/page-editor/language-menu';
import { STATE_CLASSES } from '@/data/constants/classes';
import { LOAD_PAGE } from '@/store/types/action-types';

import {
    SET_EDITOR_MODE,
    SET_PREVIEW_MODE,
    SHOW_PROMPT,
    UNLOCK_COMPONENT,
    SET_HOLD_EVENTS,
    TOGGLE_TOOLBAR,
} from '@/store/types/mutation-types';

export default {
    name: 'toolbar-editor',

    components: {
        LanguageMenu,
        Toolbar,
        ToolbarInsert,
        ToolbarOptions,
        ToolbarVertical,
    },

    props: {
        pageId: {
            type: String,
            default: '',
        },

        screen: {
            type: String,
            required: true,
        },
    },

    data() {
        return {
            lastMode: 'organisms',
            STATE_CLASSES,
            toolbarCollapseOffset: 0,
            toolbarId: 'editor',

            visible: {
                insert: true,
                options: false,
            },
        };
    },

    computed: {
        ...mapState({
            pageData: state => state.page.pageData,
            selectedComponent: state => state.page.selectedComponent,
            selectedElements: state => state.page.selectedElements,
            selectedRendering: state => state.page.selectedRendering,
            holdEvents: state => state.page.holdEvents,
        }),
    },

    watch: {
        selectedComponent() {
            this.toggleToolbar({ id: 'editor', isVisible: true });
        },

        selectedElements(elems) {
            if (elems.length) {
                this.show('options');
            } else {
                this.show('insert');
            }
        },
    },

    beforeMount() {
        this.setPreviewMode(false);
    },

    mounted() {
        this.toolbarCollapseOffset = this.$refs.toolbarVertical.$el.offsetWidth;
    },

    methods: {
        ...mapMutations({
            setEditorMode: SET_EDITOR_MODE,
            setHoldEvents: SET_HOLD_EVENTS,
            setPreviewMode: SET_PREVIEW_MODE,
            showPrompt: SHOW_PROMPT,
            toggleToolbar: TOGGLE_TOOLBAR,
            unlockComponent: UNLOCK_COMPONENT,
        }),

        ...mapActions([LOAD_PAGE]),

        enableEvents() {
            if (this.holdEvents) {
                this.setHoldEvents(false);
            }
        },

        show(target) {
            Object.keys(this.visible).forEach((key) => {
                this.visible[key] = false;
            });

            this.visible[target] = true;

            this.setEditorMode(target);
        },

        loadLanguage(lang) {
            this.loadPage({ pageId: this.pageId, lang, loading: true });
        },

        setPosition() {
            const header = document.getElementById('header');
            const toolbar = this.$refs.toolbar;

            toolbar.style.top = `${ header.offsetHeight }px`;
        },

        onUnlockComponent() {
            this.showPrompt({
                title: 'Unlock Confirmation',
                description: 'Unlocked components will no longer receive updates from the Component Editor. Do you want to continue?',
                onContinue: this.unlockComponent,
            });
        },
    },
};
</script>

<style lang="scss">
.toolbar-editor {
    $this: &;
    display: flex;
    height: 100%;

    &__column {
        display: flex;
        flex-direction: column;

        &--main {
            flex-grow: 1;
        }

        &--secondary {
            z-index: 1;
        }
    }

    &__language {
        color: var(--sidebar--languageColor);
        background-color: var(--sidebar--languageBackgroundColor);
        border-color: var(--sidebar-languageBorderColor);
    }

    &__tabs {
        display: flex;
        padding: 0;
        margin: 0;
    }

    &__tab {
        color: var(--sidebar-tabColor);
        background-color: var(--sidebar-tabBackgroundColor);
        position: relative;
        list-style: none;
        display: flex;
        flex-grow: 1;

        &:hover {
            color: var(--sidebar-tabHoverColor);
            background-color: var(--sidebar-tabHoverBackgroundColor);
            opacity: 1;
        }

        &.#{ $is-selected } {
            opacity: 1;
            color: var(--sidebar-tabSelectedColor);
            background-color: var(--sidebar-tabSelectedBackgroundColor);
        }
    }

    &__tab-button {
        border: none;
        background-color: transparent;
        color: inherit;
        width: 100%;
        font-weight: 700;
        padding: 10px;
        font-size: 14px;
    }

    &__panes {
        position: relative;
        padding: 0;
        margin: 0;
        flex-grow: 1;
        overflow: hidden;
    }

    // need to double the selector
    // so the position below overrides the one set by the vue-custom-scrollbar plugin
    &__pane#{ $this }__pane {
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        transition: transform 0.45s;
        list-style: none;
        overflow: hidden;
        height: 100%;

        &:first-child {
            transform: translateX(-100%);
        }

        &:last-child {
            transform: translateX(100%);
        }

        &.#{ $is-visible } {
            transform: translateX(0);
        }
    }

    &__options {
        @extend %fill;
    }

    &__switch {
        display: flex;
        align-items: center;
        padding: 6px 15px;
        color: var(--sidebar-navColor);
        background-color: var(--sidebar-navBackgroundColor);
    }

    &__switch-title {
        font-weight: 700;
        font-size: 13px;
        margin: 0;
    }

    &__switch-icon {
        margin-left: auto;
        font-size: 18px;
        cursor: pointer;
    }

    &__empty {
        display: flex;
        flex-direction: column;
        justify-content: center;
        align-items: center;
        text-align: center;
        padding: 40px 20px 20px;
        font-size: 15px;
    }

    &__button {
        margin-top: 30px;

        &--modify {
            @extend %button;
        }

        &--unlock {
            @extend %button-alt;
        }
    }

    &__button-icon {
        margin-right: 7px;
    }
}
</style>
